/**
 * Validation helpers — pre-condition checks for operations.
 * INTERNAL — not exported from operations/index.ts.
 */

import { getDb } from "../runtime.js"
import { ObjectType } from "../db-types.js"
import type { DbObject } from "../db-types.js"
import { fail } from "../op-types.js"
import type { OpFailure } from "../op-types.js"
import { isVirtualContainer } from "./realmHelpers.js"

/**
 * Fetch a totodo object by ID, returning it or an OpFailure.
 */
export async function assertExists(id: string): Promise<DbObject | OpFailure> {
  const db = getDb()
  const obj = await db.totodo.get(id)
  if (!obj) return fail(`Object not found: ${id}`)
  return obj
}

/**
 * Type guard: is the result an OpFailure?
 */
export function isFailed(result: unknown): result is OpFailure {
  return (
    typeof result === "object" &&
    result !== null &&
    "ok" in result &&
    (result as OpFailure).ok === false
  )
}

/**
 * Check that an object is not soft-deleted.
 */
export function assertNotDeleted(obj: DbObject): OpFailure | null {
  if (obj.isDeleted === 1) return fail(`Object is deleted: ${obj.id}`)
  return null
}

/**
 * Validate that a parentPath looks reasonable.
 */
export function assertValidParentPath(parentPath: string): OpFailure | null {
  if (parentPath === "") return null
  if (parentPath.startsWith("root/")) return null
  return fail(`Invalid parentPath: ${parentPath}`)
}

// ---------------------------------------------------------------------------
// Move targets
// ---------------------------------------------------------------------------

/** Virtual containers (no object behind them) a task can be moved to. */
const VIRTUAL_TARGETS: ReadonlySet<string> = new Set(["#inbox", "#anyday"])

/** What a destination parentPath points at. */
export type MoveTarget =
  | { kind: "root" }
  | { kind: "virtual"; segment: string }
  | { kind: "container"; obj: DbObject; depth: number }

const TARGET_HINT = "use 'root/<container-id>', 'root/#inbox' or 'root/#anyday'"

interface MoveRules {
  /** May live in root/#inbox and root/#anyday. */
  virtual: boolean
  /** May live at the top level ("root"). */
  root: boolean
  /** The kinds of container it may live in. */
  containers: readonly ObjectType[]
  label: string
}

/** Where an object of `type` may live; `undefined` = no rule beyond "an existing live container". */
function movesTo(type: ObjectType): MoveRules | undefined {
  switch (type) {
    case ObjectType.Task:
    case ObjectType.Subtask:
      return {
        virtual: true,
        root: false,
        containers: [ObjectType.Project, ObjectType.List, ObjectType.Area],
        label: "a task",
      }
    case ObjectType.Section:
      return { virtual: false, root: false, containers: [ObjectType.Project, ObjectType.List], label: "a section" }
    case ObjectType.Project:
    case ObjectType.List:
      return { virtual: false, root: true, containers: [ObjectType.Area], label: "a project or collection" }
    case ObjectType.Area:
      return { virtual: false, root: true, containers: [], label: "an area" }
    default:
      return undefined
  }
}

const TYPE_NAMES: Partial<Record<ObjectType, string>> = {
  [ObjectType.Project]: "project",
  [ObjectType.List]: "collection",
  [ObjectType.Area]: "area",
  [ObjectType.Section]: "section",
  [ObjectType.Task]: "task",
  [ObjectType.Subtask]: "task",
  [ObjectType.Checklist]: "checklist item",
}

const typeName = (type: ObjectType): string => TYPE_NAMES[type] ?? "object"

/**
 * Resolve a destination parentPath the way the app builds one: `root/<container-id>`,
 * `root/#inbox`, `root/#anyday`, or `root` (a project or area at the top level). Fails, with
 * a message for the caller, on a malformed path, an unknown virtual container, a container that
 * does not exist and one in the trash.
 */
export async function resolveMoveTarget(parentPath: unknown): Promise<MoveTarget | OpFailure> {
  if (typeof parentPath !== "string" || parentPath.length === 0) {
    return fail(`Invalid target path ${JSON.stringify(parentPath)}: ${TARGET_HINT}`)
  }
  if (parentPath === "root") return { kind: "root" }

  const segments = parentPath.split("/")
  // A virtual container is a leaf: nothing lives below root/#inbox
  if (
    segments[0] !== "root" ||
    segments.length < 2 ||
    segments.some((s) => s.trim() === "") ||
    segments.slice(1, -1).some(isVirtualContainer)
  ) {
    return fail(`Invalid target path "${parentPath}": ${TARGET_HINT}`)
  }

  const last = segments[segments.length - 1]!
  if (isVirtualContainer(last)) {
    if (segments.length === 2 && VIRTUAL_TARGETS.has(last)) return { kind: "virtual", segment: last }
    return fail(`Invalid target path "${parentPath}": unknown container "${last}" - ${TARGET_HINT}`)
  }

  const obj = await getDb().totodo.get(last)
  if (!obj) return fail(`Target container not found: ${last} (path "${parentPath}")`)
  if (obj.isDeleted === 1) return fail(`Target container is in the trash: ${last} (path "${parentPath}")`)
  return { kind: "container", obj, depth: segments.length }
}

/**
 * May `moved` be moved to `parentPath`, which resolved to `target`? Checks what would leave it
 * where no view looks (an orphan): the kind of container, not itself or one of its own
 * descendants, and a realm the caller can reach. Objects other than tasks, sections, projects,
 * collections and areas (checklist rows ...) only need an existing, live container.
 */
export function checkMoveTarget(moved: DbObject, parentPath: string, target: MoveTarget): OpFailure | null {
  const rules = movesTo(moved.type)

  if (target.kind === "root") {
    if (rules && !rules.root) return fail(`Cannot move ${rules.label} to the top level (root): ${TARGET_HINT}`)
    return null
  }

  if (target.kind === "virtual") {
    if (rules && !rules.virtual) return fail(`Cannot move ${rules.label} to ${target.segment}: only tasks live there`)
    return null
  }

  const { obj, depth } = target
  // Itself, or anything under it: the parentPath of a descendant has the object's id in it
  if (obj.id === moved.id) return fail(`Cannot move ${moved.id} into itself`)
  if (obj.parentPath.split("/").includes(moved.id as string)) {
    return fail(`Cannot move ${moved.id} into one of its own descendants (${obj.id})`)
  }

  if (rules) {
    // The app puts everything directly under `root/<container-id>`; a deeper path is where no view looks
    if (depth !== 2) return fail(`Invalid target path "${parentPath}": ${TARGET_HINT}`)
    if (!rules.containers.includes(obj.type)) {
      const accepted = [
        ...rules.containers.map(typeName),
        ...(rules.virtual ? ["the inbox", "anyday"] : []),
        ...(rules.root ? ["the top level (root)"] : []),
      ].join(", ")
      return fail(
        `Cannot move ${rules.label} into ${typeName(obj.type)} ${obj.id}: it can only go into ${accepted || "nothing"}`,
      )
    }
  }

  // A realm the caller cannot reach (another account's private realm) is never a destination.
  // Their own realm and every shared realm ("rlm...") they are a member of are.
  const realm = obj.realmId
  if (realm && realm !== getDb().cloud.currentUserId && !realm.startsWith("rlm")) {
    return fail(`Target container ${obj.id} belongs to another account`)
  }
  return null
}

/** Resolve `parentPath` and check `moved` against it. Null when the move is fine. */
export async function assertValidMoveTarget(moved: DbObject, parentPath: string): Promise<OpFailure | null> {
  const target = await resolveMoveTarget(parentPath)
  if ("ok" in target) return target
  return checkMoveTarget(moved, parentPath, target)
}
